import Button from '@components/common/button/Button';
import TextFieldController from '@components/common/dynamicFormFields/controllers/TextFieldController';
import { t } from '@hclinear/hc-linear-react';
import { StopParametersDTO } from '@model/stop/StopParametersDTO';
import {
  Alert,
  Box,
  Divider,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { Info, Trash2 } from 'lucide-react';
import React, { createContext, useContext } from 'react';
import {
  get,
  useFieldArray,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form';
import * as yup from 'yup';
import { FormFieldType } from '@/type';

export const NEXT_CONNECTION_RULE_MAX_COUNT = 5;
const CONNECTION_MAX = 100;

export enum NextConnectionRulesMode {
  KEEP = 'KEEP',
  REPLACE = 'REPLACE',
  CLEAR = 'CLEAR',
}

// the rule inputs hold strings while edited, converted to numbers on save
type NextConnectionRuleFormValue = {
  departureGapMinutes?: number | string;
  connectionMinutes?: number | string;
};

export type StopParameterisingForm = Partial<
  Omit<StopParametersDTO, 'nextConnectionRules'>
> & {
  nextConnectionRules?: NextConnectionRuleFormValue[] | null;
  nextConnectionRulesMode?: NextConnectionRulesMode;
};

export interface NextConnectionRulesFieldOptions {
  // several stops edited together: the rules only change when REPLACE or CLEAR is picked
  bulk: boolean;
  stopCount: number;
  // the stops' rules differ, nothing to show while they are kept
  mixed: boolean;
  // REPLACE starts from these
  loadedRules?: StopParametersDTO['nextConnectionRules'];
}

const isBlank = (value?: number | string | null) =>
  value === undefined || value === null || value === '';

const errorMessage = (key: string, params?: Record<string, number>) =>
  t(`stop.parameterising.nextConnectionRules.errors.${key}`, params);

// the same rules as the backend validates; base and siblings are read from the whole form
const nextConnectionRuleValidation = yup.object({
  departureGapMinutes: yup
    .string()
    .nullable()
    .test('required', t('common.form.required_field'), (value) => !isBlank(value))
    .test(
      'min',
      errorMessage('departure-gap-min'),
      (value) => isBlank(value) || Number(value) >= 1,
    )
    .test('duplicate', errorMessage('duplicate'), function (value) {
      const rules: NextConnectionRuleFormValue[] =
        this.from?.[1]?.value?.nextConnectionRules ?? [];
      return (
        isBlank(value) ||
        rules.filter((rule) => Number(rule.departureGapMinutes) === Number(value))
          .length <= 1
      );
    }),
  connectionMinutes: yup
    .string()
    .nullable()
    .test('required', t('common.form.required_field'), (value) => !isBlank(value))
    .test(
      'range',
      errorMessage('connection-range', { max: CONNECTION_MAX }),
      (value) =>
        isBlank(value) || (Number(value) >= 1 && Number(value) <= CONNECTION_MAX),
    )
    .test('max-half', function (value) {
      const gap = this.parent.departureGapMinutes;
      if (isBlank(value) || isBlank(gap)) {
        return true;
      }
      const max = Math.floor(Number(gap) / 2);
      return (
        Number(value) <= max ||
        this.createError({ message: errorMessage('max-half', { max }) })
      );
    })
    .test('below-base', function (value) {
      const base = this.from?.[1]?.value?.terminalNextConnectionMinutes;
      if (isBlank(value) || isBlank(base)) {
        return true;
      }
      return (
        Number(value) >= Number(base) ||
        this.createError({ message: errorMessage('below-base', { base: Number(base) }) })
      );
    }),
});

export const nextConnectionRulesValidation = (bulk: boolean) =>
  yup.object({
    nextConnectionRules: yup
      .array()
      .nullable()
      .when('nextConnectionRulesMode', {
        is: NextConnectionRulesMode.REPLACE,
        then: (schema) =>
          schema
            .of(nextConnectionRuleValidation)
            .max(NEXT_CONNECTION_RULE_MAX_COUNT)
            .test(
              'replace-empty',
              errorMessage('replace-empty'),
              (rules) => !bulk || (rules?.length ?? 0) > 0,
            ),
      }),
  });

const toMinutes = (value?: number | string) =>
  isBlank(value) ? undefined : Number(value);

// the number inputs' mask only accepts strings
export const toNextConnectionRuleFormValues = (
  rules?: StopParametersDTO['nextConnectionRules'],
): NextConnectionRuleFormValue[] =>
  (rules ?? []).map((rule) => ({
    departureGapMinutes: isBlank(rule.departureGapMinutes)
      ? ''
      : String(rule.departureGapMinutes),
    connectionMinutes: isBlank(rule.connectionMinutes)
      ? ''
      : String(rule.connectionMinutes),
  }));

// undefined (not sent) keeps the stops' rules
export const nextConnectionRulesToSave = ({
  nextConnectionRules,
  nextConnectionRulesMode,
}: StopParameterisingForm) => {
  switch (nextConnectionRulesMode) {
    case NextConnectionRulesMode.REPLACE:
      return (nextConnectionRules ?? []).map((rule) => ({
        departureGapMinutes: toMinutes(rule.departureGapMinutes),
        connectionMinutes: toMinutes(rule.connectionMinutes),
      }));
    case NextConnectionRulesMode.CLEAR:
      return [];
    default:
      return undefined;
  }
};

export const NextConnectionRulesDivider = () => (
  <Divider>
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {t('stop.parameterising.nextConnectionRules.title')}
      <Tooltip title={t('stop.parameterising.nextConnectionRules.note')}>
        <Info size={16} />
      </Tooltip>
    </Box>
  </Divider>
);

// the options come through a context, so the form field's component stays the same between renders
export const NextConnectionRulesOptionsContext =
  createContext<NextConnectionRulesFieldOptions>({
    bulk: false,
    stopCount: 1,
    mixed: false,
  });

export function StopNextConnectionRulesField() {
  const { bulk, stopCount, mixed, loadedRules } = useContext(
    NextConnectionRulesOptionsContext,
  );
  const { control, setValue, clearErrors } = useFormContext<StopParameterisingForm>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'nextConnectionRules',
  });
  const mode = useWatch({ control, name: 'nextConnectionRulesMode' });
  const { errors } = useFormState({ control, name: 'nextConnectionRules' });
  const listError: string | undefined = get(errors, 'nextConnectionRules.message');

  const readOnly = bulk && mode !== NextConnectionRulesMode.REPLACE;
  const showRules =
    !bulk ||
    mode === NextConnectionRulesMode.REPLACE ||
    (mode === NextConnectionRulesMode.KEEP && !mixed);

  const handleModeChange = (value: NextConnectionRulesMode | null) => {
    if (!value) {
      return;
    }
    if (value === NextConnectionRulesMode.REPLACE) {
      // starts from the common rules, empty if they differ between the stops
      setValue('nextConnectionRules', toNextConnectionRuleFormValues(loadedRules));
    }
    // the rules' errors only belong to the mode they were raised in
    clearErrors('nextConnectionRules');
    setValue('nextConnectionRulesMode', value);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {bulk && (
        <ToggleButtonGroup
          value={mode}
          exclusive
          size='small'
          fullWidth
          onChange={(_, value: NextConnectionRulesMode | null) => handleModeChange(value)}
        >
          <ToggleButton value={NextConnectionRulesMode.KEEP}>
            {t('stop.parameterising.nextConnectionRules.mode.keep')}
          </ToggleButton>
          <ToggleButton value={NextConnectionRulesMode.REPLACE}>
            {t('stop.parameterising.nextConnectionRules.mode.replace')}
          </ToggleButton>
          <ToggleButton value={NextConnectionRulesMode.CLEAR} color='error'>
            {t('stop.parameterising.nextConnectionRules.mode.clear')}
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {bulk && mode === NextConnectionRulesMode.KEEP && mixed && (
        <Alert severity='info'>
          {t('stop.parameterising.nextConnectionRules.keep-mixed')}
        </Alert>
      )}
      {bulk && mode === NextConnectionRulesMode.REPLACE && (
        <Alert severity='warning'>
          {t('stop.parameterising.nextConnectionRules.replace-warning', {
            count: stopCount,
          })}
        </Alert>
      )}
      {bulk && mode === NextConnectionRulesMode.CLEAR && (
        <Alert severity='error'>
          {t('stop.parameterising.nextConnectionRules.clear-warning', {
            count: stopCount,
          })}
        </Alert>
      )}

      {showRules && fields.length === 0 && (
        <Typography sx={{ textAlign: 'center', fontSize: '12px' }} color='textDisabled'>
          {t('stop.parameterising.nextConnectionRules.empty')}
        </Typography>
      )}
      {showRules && fields.length > 0 && (
        <Grid container spacing={2}>
          {fields.map((field, index) => (
            <React.Fragment key={field.id}>
              <Grid item xs={readOnly ? 6 : 5}>
                <TextFieldController<StopParameterisingForm>
                  control={control}
                  textField={{
                    type: FormFieldType.Number,
                    name: `nextConnectionRules.${index}.departureGapMinutes`,
                    label: 'stop.parameterising.nextConnectionRules.departure-gap',
                    postFix: t('stop.parameterising.nextConnectionRules.postfix'),
                    disabled: readOnly,
                  }}
                />
              </Grid>
              <Grid item xs={readOnly ? 6 : 5}>
                <TextFieldController<StopParameterisingForm>
                  control={control}
                  textField={{
                    type: FormFieldType.Number,
                    name: `nextConnectionRules.${index}.connectionMinutes`,
                    label: 'stop.parameterising.nextConnectionRules.connection',
                    postFix: t('stop.parameterising.nextConnectionRules.postfix'),
                    disabled: readOnly,
                  }}
                />
              </Grid>
              {!readOnly && (
                <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={t('stop.parameterising.nextConnectionRules.remove')}>
                    <IconButton color='error' onClick={() => remove(index)}>
                      <Trash2 />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </React.Fragment>
          ))}
        </Grid>
      )}

      {listError && <Alert severity='error'>{listError}</Alert>}

      {!readOnly && (
        <Box>
          <Button
            isAddButton
            variant='outlined'
            disabled={fields.length >= NEXT_CONNECTION_RULE_MAX_COUNT}
            onClick={() => append({ departureGapMinutes: '', connectionMinutes: '' })}
          >
            {t('stop.parameterising.nextConnectionRules.add', {
              max: NEXT_CONNECTION_RULE_MAX_COUNT,
            })}
          </Button>
        </Box>
      )}
    </Box>
  );
}
